import React, {useMemo, useState, useEffect} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Link, useNavigate} from 'react-router-dom';

import {DataGrid, KeenIcon} from '@/components';
import {Loading} from '@/layouts/buddy/loading';
import {DeleteConfirmationModal} from '@/pages/buddy/match/blocks/delete-match/DeleteMatchModal';
import {MatchDetailsData} from '@/pages/buddy/types/MatchDetailsData';
import {getProgressColor} from "@/pages/buddy/utils/ProgressColor.tsx";
import {formatDate} from "@/pages/buddy/utils/Date.tsx";

interface MatchesProps {
    matches: MatchDetailsData[];
    loading: boolean;
    on_delete: (matchId: string) => void;
}

const Matches: React.FC<MatchesProps> = ({matches, loading, on_delete}) => {
    const storageFilterId = 'matches-filter';
    const [isActiveFilter, setIsActiveFilter] = useState(true);

    const navigate = useNavigate();

    // Search term state
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem(storageFilterId) || '';
    });

    // Save search term to local storage
    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    // Filtered data based on search term and active status
    const filteredData = useMemo(() => {
        return matches.filter((match) => {
            const matchesSearch =
                match.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                `${match.candidate_first_name} ${match.candidate_last_name}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            const matchesActiveFilter = !isActiveFilter || match.is_active;
            return matchesSearch && matchesActiveFilter;
        });
    }, [matches, searchTerm, isActiveFilter]);

    // Modal state for delete confirmation
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<MatchDetailsData | null>(null);

    const handleDeleteClick = (match: MatchDetailsData) => {
        setSelectedMatch(match);
        setModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (selectedMatch) {
            on_delete(selectedMatch.p_id);
        }
        setModalOpen(false);
    };

    const columns = useMemo<ColumnDef<MatchDetailsData>[]>(
        () => [
            // Job column
            {
                accessorFn: (row) => row.job_name,
                id: 'job',
                header: () => 'Job',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex flex-col">
                        <Link
                            className="leading-none font-normal text-sm text-gray-700 hover:text-primary"
                            to={`/job/${info.row.original.job_p_id}`}
                        >
                            {info.row.original.job_name}
                        </Link>
                    </div>
                ),
            },
            // Candidate column
            {
                accessorFn: (row) => `${row.candidate_first_name} ${row.candidate_last_name}`,
                id: 'candidate',
                header: () => 'Candidate',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex flex-col">
                        <Link
                            className="leading-none font-normal text-sm text-gray-700 hover:text-primary"
                            to={`/candidate/${info.row.original.candidate_p_id}`}
                        >
                            {info.row.original.candidate_first_name} {info.row.original.candidate_last_name}
                        </Link>
                    </div>
                ),
            },
            // Score column with progress bar
            {
                accessorFn: (row) => row.overall_soft_score_relative,
                id: 'score',
                header: () => 'Match',
                enableSorting: true,
                cell: ({row, getValue}) => {
                    const value = getValue() as number | null;

                    if (value === null || value === undefined) {
                        return 'No score';
                    }

                    const percentage = Math.round(value * 100);

                    return (
                        <div className="flex items-center gap-2">
                            <>
                                <button
                                    className="btn btn-sm btn-icon btn-clear btn-light"
                                    onClick={() => navigate(`/match/${row.original.p_id}`)}
                                >
                                    <KeenIcon icon="eye"/>
                                </button>
                                <Link
                                    className="leading-none hover:text-primary"
                                    to={`/match/${row.original.p_id}`}
                                >
                                    {percentage}%
                                </Link>
                            </>
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div
                                    className="h-1.5 rounded-full"
                                    style={{width: `${percentage}%`, backgroundColor: getProgressColor(percentage)}}
                                ></div>
                            </div>
                        </div>
                    );
                },
                meta: {
                    className: 'w-[250px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            // Last Modified column
            {
                accessorFn: (row) => row.updated_at,
                id: 'lastModified',
                enableSorting: true,
                header: () => 'Last Modified',
                cell: (info) => {
                    const value = info.getValue() as string | null;
                    return formatDate(value);
                },
                meta: {
                    className: 'w-[350px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            // Open column
            {
                id: 'edit',
                header: () => 'Open',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/match/${row.original.p_id}`)}
                    >
                        <KeenIcon icon="notepad-edit"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },
            // Delete column
            /*{
                id: 'delete',
                header: () => 'Delete',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => handleDeleteClick(row.original)}
                    >
                        <KeenIcon icon="trash"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },*/
        ],
        [navigate]
    );

    if (loading) return <Loading loading={loading}/>;

    return (
        <>
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-5">
                    <h3 className="card-title">Matches</h3>
                    <div className="flex gap-6">
                        <div className="relative">
                            <KeenIcon
                                icon="magnifier"
                                className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                            />
                            <input
                                type="text"
                                placeholder="Search Matches"
                                className="input input-sm pl-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <label className="switch switch-sm">
                            <input
                                name="check"
                                type="checkbox"
                                checked={isActiveFilter}
                                onChange={() => setIsActiveFilter(!isActiveFilter)}
                                className="order-2"
                            />
                            <span className="switch-label order-1">Only Active Matches</span>
                        </label>
                    </div>
                </div>
                <div className="card-body">
                    <DataGrid
                        key={`${searchTerm}-${isActiveFilter}`}
                        columns={columns}
                        data={filteredData}
                        pagination={{size: 10}}
                        sorting={[{id: 'job', desc: false}]}
                    />
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {selectedMatch && (
                <DeleteConfirmationModal
                    open={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                    matchName={`${selectedMatch.job_name} - ${selectedMatch.candidate_first_name} ${selectedMatch.candidate_last_name}`}
                />
            )}
        </>
    );
};

export {Matches};
